import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import type { MutationResponse } from '@/types/api/mutations';
import type { TFunction } from 'i18next';
import type {
  DefaultValues,
  FieldValues,
  Resolver,
  UseFormReturn,
} from 'react-hook-form';
import type { ZodType } from 'zod';

import {
  processErrorResponse,
  processSuccessResponse,
} from '@/lib/processApiResponse';

import { HttpClient } from '@/network/HttpClient';

// --- Types ---

type UseFormPageConfig<
  TValues extends FieldValues,
  TId extends number | string = number,
> = {
  /** Schema factory — hook passes `t` and `isEditMode` automatically */
  schema: (t: TFunction, isEditMode: boolean) => ZodType;

  /** Form default values (inline, entity-dependent) */
  defaultValues: DefaultValues<TValues>;

  /** Data transformation before submit — returns FormData (file uploads) or plain object */
  transform: (values: TValues, id?: TId) => FormData | Record<string, unknown>;

  /** Entity ID — determines mode (create vs edit) and submit URL. Defaults to `number`; pass `TId = string` for UUID PKs */
  entityId?: TId;

  /** API endpoint (base URL, hook appends `/{id}` for edit) */
  api_url: string;

  /** Redirect URL after successful submit */
  redirectUrl: string;

  /** Extract entity ID from response and redirect to `{redirectUrl}/{id}` */
  entityIdKey?: string;

  /** Map server error field names to form field names (e.g. `{ locations: 'location_ids' }`) */
  errorFieldMap?: Partial<Record<string, keyof TValues & string>>;

  /** Custom success handler — when provided, replaces default processSuccessResponse + redirect */
  onSuccess?: (response: MutationResponse) => void;
};

type UseFormPageReturn<TValues extends FieldValues> = {
  /** react-hook-form instance — pass to `<Form form={form}>` */
  form: UseFormReturn<TValues>;
  /** Submit handler — pass to `<FormWrapper onSubmit={onSubmit}>` */
  onSubmit: () => void;
  /** Mutation loading state — pass to `<FormWrapper isPending={isPending}>` */
  isPending: boolean;
  /** `'create'` or `'edit'` — pass to `<FormWrapper mode={mode}>` */
  mode: 'create' | 'edit';
  /** Shorthand for `mode === 'edit'` */
  isEditMode: boolean;
  /** Translation function — use for field labels and titles */
  t: TFunction;
};

// --- Mutation types ---

type FormMutationParams = {
  url: string;
  method: 'POST' | 'PUT';
  data: FormData | Record<string, unknown>;
};

// --- Hook ---

/**
 * All-in-one hook for form pages: form state, mutation, submit, mode detection, error handling.
 *
 * Generic `TValues` must match the Zod schema's inferred type. Pass it explicitly when
 * the component needs typed `form.control` (e.g. for `useWatch` or child components):
 * `useFormPage<ServiceFormValues>({...})`. When omitted, TypeScript infers from `defaultValues`.
 *
 * Note: the spec originally used `TSchema extends ZodType` with `z.infer<TSchema>` to derive
 * form values. This was changed to `TValues extends FieldValues` because `z.infer<ZodType>`
 * resolves to `any`, making it impossible for TypeScript to prove `z.infer<TSchema> extends FieldValues`
 * — which `useForm`, `zodResolver`, and `handleSubmit` all require.
 *
 * @example
 * ```tsx
 * const { form, onSubmit, isPending, mode, t } = useFormPage<TrainerFormValues>({
 *   schema: createTrainerFormSchema,
 *   transform: createTrainerFormData,
 *   entityId: trainer?.id,
 *   api_url,
 *   redirectUrl: url,
 *   defaultValues: {
 *     first_name: trainer?.first_name || '',
 *     active: trainer?.active ?? true,
 *   },
 * })
 *
 * <FormWrapper mode={mode} title={t('Trainer')} onSubmit={onSubmit}
 *              backUrl={url} isPending={isPending}>
 *   <Form form={form}>
 *     <Form.Input name="first_name" label={t('First Name')} isRequired />
 *   </Form>
 * </FormWrapper>
 * ```
 */
export const useFormPage = <
  TValues extends FieldValues,
  TId extends number | string = number,
>(
  config: UseFormPageConfig<TValues, TId>,
): UseFormPageReturn<TValues> => {
  const {
    schema: schemaFactory,
    defaultValues,
    transform,
    entityId,
    api_url,
    redirectUrl,
    entityIdKey,
    errorFieldMap,
    onSuccess: customOnSuccess,
  } = config;

  const { t } = useTranslation();

  const mode = entityId ? 'edit' : 'create';

  const schema = useMemo(
    () => schemaFactory(t, mode === 'edit'),
    [schemaFactory, t, mode],
  );

  const form = useForm<TValues>({
    // zodResolver's overloads require FieldValues on the schema's _input type.
    // ZodType has _input: unknown, so we cast through unknown to satisfy the constraint.
    resolver: zodResolver(
      schema as unknown as Parameters<typeof zodResolver>[0],
    ) as Resolver<TValues>,
    defaultValues,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (params: FormMutationParams) =>
      HttpClient.request<MutationResponse>({
        method: params.method,
        url: params.url,
        data: params.data,
      }),
  });

  const onSubmit = useCallback(() => {
    form.handleSubmit((values: TValues) => {
      const submitUrl = entityId ? `${api_url}/${entityId}` : api_url;
      const method = entityId ? 'PUT' : 'POST';
      const data = transform(values, entityId);

      mutate(
        { url: submitUrl, method, data },
        {
          onSuccess: (response) => {
            if (!response) return;
            if (customOnSuccess) {
              customOnSuccess(response);
            } else {
              processSuccessResponse({
                response,
                manualRedirectUrl: redirectUrl,
                entityIdKey,
              });
            }
          },
          onError: (error) =>
            processErrorResponse({
              error,
              form,
              errorFieldMap,
            }),
        },
      );
    })();
  }, [
    entityId,
    api_url,
    transform,
    redirectUrl,
    entityIdKey,
    errorFieldMap,
    customOnSuccess,
    form,
    mutate,
  ]);

  const isEditMode = mode === 'edit';

  return { form, onSubmit, isPending, mode, isEditMode, t };
};
